import { Component, OnInit, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import { RegisterClass } from '@memberjunction/global';
import { BaseResourceComponent } from '@memberjunction/ng-shared';
import { RunQuery } from '@memberjunction/core';
import { BCBaseComponent } from '@bluecypress/bcsaas-ng-core';

// ── Types ──

interface CostSummary {
  totalCost: number;
  totalTokens: number;
  totalPromptTokens: number;
  totalCompletionTokens: number;
  totalCacheReadTokens: number;
  avgCostPerRun: number;
  totalPromptRuns: number;
  cacheHitRate: number;
}

interface ModelCost {
  modelName: string;
  vendorName: string;
  totalCost: number;
  totalTokens: number;
  runCount: number;
}

interface OrgCost {
  organizationName: string;
  totalCost: number;
  totalTokens: number;
  runCount: number;
}

interface DailyCost {
  day: string;
  totalCost: number;
  totalTokens: number;
  runCount: number;
}

type TimePreset = 'this-month' | 'last-month' | '7d' | 'all-time' | 'custom';

interface TimePresetOption {
  key: TimePreset;
  label: string;
}

@Component({
  standalone: false,
  selector: 'skip-monitoring-costs',
  templateUrl: './costs.component.html',
  styleUrls: ['./costs.component.css']
})
@RegisterClass(BaseResourceComponent, 'SkipMonitoringCostsResource')
export class SkipMonitoringCostsComponent extends BCBaseComponent implements OnInit, OnDestroy {

  // Loading / error
  Loading = false;
  Error: string | null = null;

  // Time preset
  SelectedPreset: TimePreset = 'this-month';
  CustomStartDate = '';
  CustomEndDate = '';
  TimePresets: TimePresetOption[] = [
    { key: 'this-month', label: 'This Month' },
    { key: 'last-month', label: 'Last Month' },
    { key: '7d', label: 'Last 7 Days' },
    { key: 'all-time', label: 'All Time' },
    { key: 'custom', label: 'Custom' }
  ];

  // Data
  Summary: CostSummary | null = null;
  ModelCosts: ModelCost[] = [];
  OrgCosts: OrgCost[] = [];
  DailyCosts: DailyCost[] = [];

  // Chart computed data
  FilledDailyCosts: DailyCost[] = [];
  MaxDailyCost = 0;
  NiceMaxCost = 0;

  private cdr = inject(ChangeDetectorRef);

  async GetResourceDisplayName(): Promise<string> {
    return 'System Costs & Usage';
  }

  async GetResourceIconClass(): Promise<string> {
    return 'fa-solid fa-coins';
  }

  override async ngOnInit(): Promise<void> {
    super.ngOnInit();
    await this.LoadData();
    this.NotifyLoadComplete();
  }

  // ========== Time Preset ==========

  SelectPreset(preset: TimePreset): void {
    this.SelectedPreset = preset;
    this.LoadData();
  }

  ApplyCustomRange(): void {
    this.SelectedPreset = 'custom';
    this.LoadData();
  }

  private getDateRange(): { start: string; end: string } {
    const now = new Date();
    let start: Date;
    let end: Date = now;

    switch (this.SelectedPreset) {
      case 'this-month':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'last-month':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case '7d':
        start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'all-time':
        start = new Date(2020, 0, 1);
        break;
      case 'custom':
        if (this.CustomStartDate && this.CustomEndDate) {
          return {
            start: new Date(this.CustomStartDate).toISOString(),
            end: new Date(this.CustomEndDate).toISOString()
          };
        }
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
    }

    return { start: start.toISOString(), end: end.toISOString() };
  }

  // ========== Data Loading ==========

  async LoadData(): Promise<void> {
    this.Loading = true;
    this.Error = null;
    this.cdr.detectChanges();

    try {
      const { start, end } = this.getDateRange();
      const rq = new RunQuery();
      const result = await rq.RunQuery({
        QueryName: 'MonitoringSystemCosts',
        CategoryPath: '/Skip/Monitoring',
        Parameters: { StartDate: start, EndDate: end }
      });

      this.resetData();

      if (result.Success && result.Results?.length) {
        this.parseResults(result.Results);
      }

      this.buildFilledDailyCosts();
    } catch (e: unknown) {
      this.Error = e instanceof Error ? e.message : 'Failed to load cost data';
    } finally {
      this.Loading = false;
      this.cdr.detectChanges();
    }
  }

  private resetData(): void {
    this.Summary = null;
    this.ModelCosts = [];
    this.OrgCosts = [];
    this.DailyCosts = [];
    this.MaxDailyCost = 0;
  }

  private parseResults(results: Record<string, unknown>[]): void {
    for (const row of results) {
      const granularity = row['Granularity'] as string;
      switch (granularity) {
        case 'Summary':
          this.parseSummaryRow(row);
          break;
        case 'Model':
          this.parseModelRow(row);
          break;
        case 'Organization':
          this.parseOrgRow(row);
          break;
        case 'Daily':
          this.parseDailyRow(row);
          break;
      }
    }
    this.ModelCosts.sort((a, b) => b.totalCost - a.totalCost);
    this.OrgCosts.sort((a, b) => b.totalCost - a.totalCost);
  }

  private parseSummaryRow(row: Record<string, unknown>): void {
    this.Summary = {
      totalCost: (row['TotalCost'] as number) || 0,
      totalTokens: (row['TotalTokens'] as number) || 0,
      totalPromptTokens: (row['TotalPromptTokens'] as number) || 0,
      totalCompletionTokens: (row['TotalCompletionTokens'] as number) || 0,
      totalCacheReadTokens: (row['TotalCacheReadTokens'] as number) || 0,
      avgCostPerRun: (row['AvgCostPerRun'] as number) || 0,
      totalPromptRuns: (row['TotalPromptRuns'] as number) || 0,
      cacheHitRate: (row['CacheHitRate'] as number) || 0
    };
  }

  private parseModelRow(row: Record<string, unknown>): void {
    this.ModelCosts.push({
      modelName: (row['ModelName'] as string) || 'Unknown',
      vendorName: (row['VendorName'] as string) || 'Unknown',
      totalCost: (row['TotalCost'] as number) || 0,
      totalTokens: (row['TotalTokens'] as number) || 0,
      runCount: (row['TotalPromptRuns'] as number) || 0
    });
  }

  private parseOrgRow(row: Record<string, unknown>): void {
    this.OrgCosts.push({
      organizationName: (row['OrganizationName'] as string) || 'Unknown',
      totalCost: (row['TotalCost'] as number) || 0,
      totalTokens: (row['TotalTokens'] as number) || 0,
      runCount: (row['TotalPromptRuns'] as number) || 0
    });
  }

  private parseDailyRow(row: Record<string, unknown>): void {
    const cost = (row['TotalCost'] as number) || 0;
    this.DailyCosts.push({
      day: row['Day'] as string,
      totalCost: cost,
      totalTokens: (row['TotalTokens'] as number) || 0,
      runCount: (row['TotalPromptRuns'] as number) || 0
    });
    if (cost > this.MaxDailyCost) {
      this.MaxDailyCost = cost;
    }
  }

  // ========== Chart Data Builders ==========

  private buildFilledDailyCosts(): void {
    const { start, end } = this.getDateRange();
    const startDate = new Date(start);
    const endDate = new Date(end);
    const costMap = this.buildDayCostMap();

    this.FilledDailyCosts = [];
    const current = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const limit = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

    while (current <= limit) {
      const key = this.toDayKey(current);
      const existing = costMap.get(key);
      if (existing) {
        this.FilledDailyCosts.push(existing);
      } else {
        this.FilledDailyCosts.push({
          day: current.toISOString(),
          totalCost: 0,
          totalTokens: 0,
          runCount: 0
        });
      }
      current.setDate(current.getDate() + 1);
    }

    this.NiceMaxCost = this.computeNiceMax(this.MaxDailyCost);
  }

  private buildDayCostMap(): Map<string, DailyCost> {
    const map = new Map<string, DailyCost>();
    for (const row of this.DailyCosts) {
      const d = new Date(row.day);
      map.set(this.toDayKey(d), row);
    }
    return map;
  }

  private toDayKey(d: Date): string {
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  }

  private computeNiceMax(maxValue: number): number {
    if (maxValue <= 0) return 1;
    const magnitude = Math.pow(10, Math.floor(Math.log10(maxValue)));
    const normalized = maxValue / magnitude;
    if (normalized <= 1) return magnitude;
    if (normalized <= 2) return 2 * magnitude;
    if (normalized <= 2.5) return 2.5 * magnitude;
    if (normalized <= 5) return 5 * magnitude;
    return 10 * magnitude;
  }

  // ========== Chart Helpers ==========

  get CostYAxisTicks(): number[] {
    if (!this.NiceMaxCost) return [0];
    const step = this.NiceMaxCost / 4;
    return [0, step, step * 2, step * 3, this.NiceMaxCost];
  }

  GetCostBarHeight(cost: number): number {
    if (!this.NiceMaxCost || !cost) return 0;
    return (cost / this.NiceMaxCost) * 100;
  }

  GetGridlinePosition(tick: number, niceMax: number): number {
    if (!niceMax) return 0;
    return (tick / niceMax) * 100;
  }

  GetHBarWidth(cost: number, maxCost: number): number {
    if (!maxCost || !cost) return 0;
    return (cost / maxCost) * 100;
  }

  GetShortDay(iso: string): string {
    if (!iso) return '';
    return new Date(iso).getDate().toString();
  }

  // ========== Token Distribution ==========

  get TotalTokensForDistribution(): number {
    if (!this.Summary) return 0;
    return this.Summary.totalPromptTokens + this.Summary.totalCompletionTokens + this.Summary.totalCacheReadTokens;
  }

  GetTokenSegmentWidth(tokens: number): number {
    const total = this.TotalTokensForDistribution;
    if (!total || !tokens) return 0;
    return (tokens / total) * 100;
  }

  // ========== Model/Org Chart Helpers ==========

  get MaxModelCost(): number {
    if (this.ModelCosts.length === 0) return 0;
    return this.ModelCosts[0].totalCost;
  }

  get MaxOrgCost(): number {
    if (this.OrgCosts.length === 0) return 0;
    return this.OrgCosts[0].totalCost;
  }

  GetModelCostPercent(cost: number): number {
    if (!this.Summary?.totalCost || !cost) return 0;
    return (cost / this.Summary.totalCost) * 100;
  }

  GetOrgCostPercent(cost: number): number {
    if (!this.Summary?.totalCost || !cost) return 0;
    return (cost / this.Summary.totalCost) * 100;
  }

  // ========== Display Formatters ==========

  FormatCost(cost: number): string {
    if (!cost) return '$0.00';
    return `$${cost.toFixed(2)}`;
  }

  FormatCostPrecise(cost: number): string {
    if (!cost) return '$0.0000';
    return `$${cost.toFixed(4)}`;
  }

  FormatAxisCost(cost: number): string {
    if (cost === 0) return '$0';
    if (cost >= 100) return `$${Math.round(cost)}`;
    if (cost >= 10) return `$${cost.toFixed(1)}`;
    return `$${cost.toFixed(2)}`;
  }

  FormatTokens(count: number): string {
    if (!count) return '0';
    if (count >= 1e6) return `${(count / 1e6).toFixed(1)}M`;
    if (count >= 1e3) return `${(count / 1e3).toFixed(1)}K`;
    return count.toString();
  }

  FormatNumber(count: number): string {
    if (!count) return '0';
    return count.toLocaleString();
  }

  FormatPercent(value: number): string {
    if (!value) return '0.0%';
    return `${value.toFixed(1)}%`;
  }

  FormatDay(iso: string): string {
    if (!iso) return '\u2014';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', weekday: 'short' });
  }
}
